import * as Sentry from '@sentry/react'
import React, { FC, memo, Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'

import { RequireAuth, useAuth } from '@affiliate-cabinet/auth'
import { useI18n } from '@affiliate-cabinet/i18n'
import { gApageView } from '@affiliate-frontend-analytics/events'
import { Grid, Loader } from '@affstore-ds/uikit'

import { renderRoutingGuest, renderRoutingProtected, renderSharedRoutes } from '../pages'
import { NotFound } from '../pages/404'
import { InternalError } from '../pages/500'
import { routes } from '../shared/routes'
import { getStartPage } from '../shared/utils/getStartPage'
import { LayoutGuest, LayoutLoader, LayoutMain } from './layout'
import { AppProviders } from './providers'

import { useOldLoginGuide } from '../shared/providers/TransferGuideContainer/hooks/useOldLoginGuide'

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

export const AppEntry: FC = memo(() => {
  const {
    auth: { user },
  } = useAuth()
  const { i18n } = useI18n()
  const location = useLocation()

  useOldLoginGuide()

  const getNavigationPath = () => {
    if (!user) {
      return `${routes.ROUTES_AUTH.signIn}${location.search}`
    }

    if (!user?.is_onboarding_passed) {
      return `${routes.ROUTES_ONBOARDING.onboarding}${location.search}`
    }

    return `${getStartPage()}${location.search}`
  }

  useEffect(() => {
    gApageView(location.pathname + location.search)
  }, [location])

  useEffect(() => {
    if (user?.id) {
      Sentry.setUser({ id: user.id.toString(), language: i18n.locale() })
    } else {
      Sentry.setUser(null)
    }
  }, [i18n, user?.id])

  return (
    <LayoutLoader>
      <Helmet>
        <meta name="googlebot" content="noindex" data-rh="true" />
      </Helmet>
      <SentryRoutes>
        <Route element={<Navigate to={getNavigationPath()} />} path="/" />

        <Route element={<LayoutGuest />} path="/">
          {renderRoutingGuest()}
        </Route>

        <Route
          element={
            <RequireAuth signInRoute={routes.ROUTES_AUTH.signIn}>
              <LayoutMain />
            </RequireAuth>
          }
          path="/"
        >
          {renderRoutingProtected()}
          {user && (
            <>
              <Route element={<NotFound />} path={routes.ROUTES_ERROR['404']} />
              <Route element={<InternalError />} path={routes.ROUTES_ERROR['500']} />
              <Route element={<Navigate to={routes.ROUTES_ERROR['404']} />} path="*" />
            </>
          )}
        </Route>

        <Route
          element={
            <Suspense fallback={<Loader />}>
              <Grid.Container>
                <Outlet />
              </Grid.Container>
            </Suspense>
          }
        >
          {renderSharedRoutes()}
        </Route>

        {!user && (
          <>
            <Route element={<NotFound />} path={routes.ROUTES_ERROR['404']} />
            <Route element={<InternalError />} path={routes.ROUTES_ERROR['500']} />
            <Route element={<Navigate to={routes.ROUTES_ERROR['404']} />} path="*" />
          </>
        )}
      </SentryRoutes>
    </LayoutLoader>
  )
})

AppEntry.displayName = 'AppEntry'

const App = memo(() => (
  <AppProviders>
    <AppEntry />
  </AppProviders>
))

App.displayName = 'App'

export default App
